import { ReactNode } from 'react';
import { GetServerSideProps, NextPage } from 'next';
import {
  CITY_SLUGS,
  NEIGHBHORHOOD_SLUGS,
  PageViewTracker,
} from '@belong/common';
import { kebabCase } from 'es-toolkit/compat';
import { fetchHomeService, fetchRelatedHomesService } from 'src/api/homes';
import { HousePageMeta } from 'src/features/house-page/components/house-page-meta/house-page-meta';
import {
  HousePage,
  HousePageProps,
} from 'src/features/house-page/components/house-page/house-page';
import { HouseLayout } from 'src/features/house-page/components/layout/layout';
import { ApiError } from 'src/utils/build-api-client';
import { buildServerClient } from 'src/utils/server-client';

type StaticMethods = {
  getLayout?: (page: ReactNode) => ReactNode;
};

const _HousePage: NextPage<HousePageProps> & StaticMethods = ({
  home,
  relatedUnits,
  hasCityLpCard,
  hasNeighborhoodLpCard,
}) => {
  // `!` here and on the `isPublished` read in `getServerSideProps`: both
  // dereference `basicInfo` unconditionally today.
  return (
    <PageViewTracker name="house_page" homeId={home.basicInfo!.unitId}>
      <HousePageMeta home={home} />
      <HousePage
        home={home}
        relatedUnits={relatedUnits}
        hasCityLpCard={hasCityLpCard}
        hasNeighborhoodLpCard={hasNeighborhoodLpCard}
      />
    </PageViewTracker>
  );
};

_HousePage.getLayout = HouseLayout;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { homeId: homeIdParam, slug: slugParam } = ctx.query;

  // Same single-segment narrowing as the other dynamic routes: `query` types
  // each segment as `string | string[]` because catch-all routes share the
  // type, and the reads below all want a plain `string`.
  const homeId =
    typeof homeIdParam === 'string' ? homeIdParam : (homeIdParam?.[0] ?? '');
  const slug =
    typeof slugParam === 'string' ? slugParam : (slugParam?.[0] ?? '');

  const apiClient = buildServerClient(ctx);

  try {
    const [home, relatedHomesResult] = await Promise.all([
      fetchHomeService(homeId, apiClient),
      fetchRelatedHomesService(homeId, apiClient),
    ]);

    if (!home?.basicInfo!.isPublished) {
      return {
        notFound: true,
      };
    }

    const hasCityLpCard = CITY_SLUGS.includes(slug);
    const hasNeighborhoodLpCard =
      // Cast because `slug` is a route segment: a slug with no entry misses and
      // falls through to the `|| false` below, as before.
      NEIGHBHORHOOD_SLUGS[slug as keyof typeof NEIGHBHORHOOD_SLUGS]?.includes(
        kebabCase(home.address?.neighborhood ?? ''),
      ) || false;

    return {
      props: {
        home,
        // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
        // The paginated response's field is `items`, not `units`, so this has
        // always resolved to `[]` and the related-homes section has never
        // rendered anything. Suppressed rather than corrected, because
        // correcting it turns that section on.
        relatedUnits: relatedHomesResult?.units || [],
        hasCityLpCard,
        hasNeighborhoodLpCard,
      },
    };
  } catch (e) {
    if (e instanceof ApiError && e?.status === 404) {
      return {
        notFound: true,
      };
    }
    // Fallback: return notFound for any other errors to avoid crashes
    return {
      notFound: true,
    };
  }
};

export default _HousePage;
